import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  message,
  Modal,
  Drawer,
  Tag,
  Typography,
  Row,
  Col,
  Input,
  Select,
  Switch,
  Popconfirm,
  Tooltip,
  Upload,
  Divider,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  DownloadOutlined,
  UploadOutlined,
  EyeOutlined,
  SettingOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { documentTypesAPI } from '../services/api';
import DocumentTypeEditor from '../components/DocumentTypeEditor';
import DocumentTypeViewer from '../components/DocumentTypeViewer';
import { useAuthStore } from '../store/authStore';
import AppHeader from '../components/Header';
import LoadingAnimation from '../components/LoadingAnimation';
import { API_BASE_URL } from '../config/environment';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

interface DocumentType {
  id: string;
  document_type: string;
  description: string;
  schema: {
    version: string;
    fields: Array<{
      name: string;
      type: string;
      required: boolean;
      description?: string;
      validation_pattern?: string;
      extraction_rules?: any;
    }>;
  };
  settings: {
    ocr_engine?: string;
    confidence_threshold?: number;
    auto_correct?: boolean;
    review_required?: boolean;
  };
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by?: string;
  is_active: boolean;
  client_id: string;
  version: number;
}

const DocumentTypesPage: React.FC = () => {
  const { user, token } = useAuthStore();
  const navigate = useNavigate();
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | null>(null);
  const [editorVisible, setEditorVisible] = useState(false);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [sourceDocumentType, setSourceDocumentType] = useState<DocumentType | null>(null);
  const [newDocumentTypeName, setNewDocumentTypeName] = useState('');
  const [cloneLoading, setCloneLoading] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [testingDocumentType, setTestingDocumentType] = useState<DocumentType | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const loadingRef = useRef(false);

  // Load document types
  const loadDocumentTypes = async () => {
    if (loadingRef.current) return;
    
    try {
      loadingRef.current = true;
      setLoading(true);
      const response = await documentTypesAPI.getDocumentTypes(false); // Always get all document types
      console.log('Document types response:', response.data);
      
      if (response.data.success) {
        let types = response.data.data || [];
        console.log('All document types from API:', types);
        
        // Filter by search term
        if (searchTerm && searchTerm.trim()) {
          types = types.filter((type: DocumentType) =>
            type.document_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (type.description && type.description.toLowerCase().includes(searchTerm.toLowerCase()))
          );
        }
        
        console.log('Final filtered document types:', types);
        console.log('Search term:', searchTerm);
        setDocumentTypes(types);
      } else {
        console.error('API response not successful:', response.data);
        message.error('Failed to load document types: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to load document types:', error);
      message.error('Failed to load document types');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    loadDocumentTypes();
  }, [searchTerm]);

  // Handle create new document type
  const handleCreate = () => {
    setSelectedDocumentType(null);
    setIsCreating(true);
    setEditorVisible(true);
  };

  // Handle edit document type
  const handleEdit = (documentType: DocumentType) => {
    setSelectedDocumentType(documentType);
    setIsCreating(false);
    setEditorVisible(true);
  };

  // Handle view document type
  const handleView = (documentType: DocumentType) => {
    setSelectedDocumentType(documentType);
    setViewerVisible(true);
  };

  // Handle clone document type
  const handleClone = (documentType: DocumentType) => {
    setSourceDocumentType(documentType);
    setNewDocumentTypeName(`${documentType.document_type}_copy`);
    setCloneModalVisible(true);
  };

  // Handle clone confirm
  const handleCloneConfirm = async () => {
    if (!sourceDocumentType || !newDocumentTypeName.trim()) {
      message.error('Please enter a name for the cloned document type');
      return;
    }

    try {
      setCloneLoading(true);
      
      const response = await documentTypesAPI.cloneDocumentType({
        source_document_type: sourceDocumentType.document_type,
        target_document_type: newDocumentTypeName.trim(),
        description: `Copy of ${sourceDocumentType.description}`,
        copy_settings: true
      });

      if (response.data.success) {
        const actualName = response.data.data?.document_type || newDocumentTypeName.trim();
        message.success(`Document type "${actualName}" cloned successfully`);
        
        // Close modal and reset state
        setCloneModalVisible(false);
        setNewDocumentTypeName('');
        setSourceDocumentType(null);
        
        // Clear search and reload
        setSearchTerm('');
        
        // Force immediate reload without delay
        await loadDocumentTypes();
      }
    } catch (error: any) {
      console.error('Failed to clone document type:', error);
      const errorMessage = error.response?.data?.message || 'Failed to clone document type';
      message.error(errorMessage);
    } finally {
      setCloneLoading(false);
    }
  };

  // Handle clone cancel
  const handleCloneCancel = () => {
    setCloneModalVisible(false);
    setNewDocumentTypeName('');
    setSourceDocumentType(null);
    setCloneLoading(false);
  };

  // Handle delete document type
  const handleDelete = async (documentType: DocumentType) => {
    try {
      const response = await documentTypesAPI.deleteDocumentType(documentType.document_type);
      if (response.data.success) {
        message.success('Document type deleted successfully');
        loadDocumentTypes();
      }
    } catch (error) {
      console.error('Failed to delete document type:', error);
      message.error('Failed to delete document type');
    }
  };

  // Handle export document type
  const handleExport = async (documentType: DocumentType) => {
    try {
      const response = await documentTypesAPI.exportDocumentType(documentType.document_type);
      if (response.data.success) {
        const blob = new Blob([JSON.stringify(response.data.data, null, 2)], {
          type: 'application/json'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${documentType.document_type}_schema.json`;
        link.click();
        window.URL.revokeObjectURL(url);
        message.success('Document type exported successfully');
      }
    } catch (error) {
      console.error('Failed to export document type:', error);
      message.error('Failed to export document type');
    }
  };

  // Handle import document type
  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        const schema = JSON.parse(text);
        
        const response = await documentTypesAPI.importDocumentType({
          schema,
          overwrite: false
        });

        if (response.data.success) {
          message.success('Document type imported successfully');
          loadDocumentTypes();
        }
      } catch (error) {
        console.error('Failed to import document type:', error);
        message.error('Failed to import document type');
      }
    };
    input.click();
  };

  // Handle test document type
  const handleTest = (documentType: DocumentType) => {
    setTestingDocumentType(documentType);
    setTestResults(null);
    setUploadedFile(null);
    setTestModalVisible(true);
  };

  // Handle test execution
  const handleTestExecution = async () => {
    if (!testingDocumentType) {
      message.error('No document type selected for testing');
      return;
    }

    if (!uploadedFile) {
      message.error('Please upload a document to test');
      return;
    }

    try {
      setTestLoading(true);
      
      // Create FormData for file upload with the correct field name expected by backend
      const formData = new FormData();
      formData.append('sample_file', uploadedFile); // Backend expects 'sample_file'
      formData.append('test_fields', JSON.stringify({})); // Additional test configuration

      // Use fetch API for file upload with proper headers
      const response = await fetch(
        `${API_BASE_URL}/api/document-types/${testingDocumentType.document_type}/test`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            // Don't set Content-Type header - let browser set it for FormData
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success) {
        setTestResults(result.data);
        message.success('Document testing completed successfully');
      } else {
        message.error('Document testing failed: ' + (result.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Failed to test document:', error);
      message.error('Failed to test document: ' + (error.message || 'Unknown error'));
    } finally {
      setTestLoading(false);
    }
  };

  // Handle test modal close
  const handleTestModalClose = () => {
    setTestModalVisible(false);
    setTestingDocumentType(null);
    setTestResults(null);
    setUploadedFile(null);
    setTestLoading(false);
  };

  // Table columns
  const columns = [
    {
      title: 'Document Type',
      dataIndex: 'document_type',
      key: 'document_type',
      sorter: (a: DocumentType, b: DocumentType) => a.document_type.localeCompare(b.document_type),
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Fields',
      key: 'fields_count',
      render: (record: DocumentType) => (
        <Tag color="blue">{record.schema.fields.length} fields</Tag>
      )
    },
    {
      title: 'Version',
      dataIndex: ['schema', 'version'],
      key: 'version',
      render: (version: string) => <Tag>{version}</Tag>
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 300,
      render: (record: DocumentType) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <>
              <Tooltip title="Edit">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
              
              <Tooltip title="Clone">
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => handleClone(record)}
                />
              </Tooltip>
              
              <Tooltip title="Test">
                <Button
                  type="text"
                  icon={<PlayCircleOutlined />}
                  onClick={() => handleTest(record)}
                />
              </Tooltip>
              
              <Popconfirm
                title="Are you sure you want to delete this document type?"
                onConfirm={() => handleDelete(record)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="Delete">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <Layout className="layout-container">
      <AppHeader />
      <Content style={{ padding: '24px', minHeight: 'calc(100vh - 64px)' }}>
        <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
          <Col>
            <Space align="center">
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/dashboard')}
                type="text"
                size="large"
              />
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  Document Types
                </Title>
                <Text type="secondary">
                  Manage document schemas and field extraction rules
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              {(user?.role === 'admin' || user?.role === 'super_admin') && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                >
                  Create Document Type
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={18}>
            <Input
              placeholder="Search document types..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={6}>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadDocumentTypes}
              loading={loading}
            >
              Refresh
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={documentTypes}
          rowKey="document_type"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} document types`
          }}
        />
      </Card>

      {/* Document Type Editor */}
      <Drawer
        title={isCreating ? 'Create Document Type' : 'Edit Document Type'}
        width={800}
        open={editorVisible}
        onClose={() => setEditorVisible(false)}
        destroyOnClose
      >
        <DocumentTypeEditor
          documentType={selectedDocumentType}
          isCreating={isCreating}
          onSuccess={() => {
            setEditorVisible(false);
            loadDocumentTypes();
          }}
          onCancel={() => setEditorVisible(false)}
        />
      </Drawer>

      {/* Document Type Viewer */}
      <Drawer
        title="Document Type Details"
        width={900}
        open={viewerVisible}
        onClose={() => setViewerVisible(false)}
        destroyOnClose
      >
        {selectedDocumentType && (
          <DocumentTypeViewer documentType={selectedDocumentType} />
        )}
      </Drawer>

      {/* Clone Document Type Modal */}
      <Modal
        title="Clone Document Type"
        open={cloneModalVisible}
        onOk={handleCloneConfirm}
        onCancel={handleCloneCancel}
        okText={cloneLoading ? "Cloning..." : "Clone"}
        cancelText="Cancel"
        width={500}
        confirmLoading={cloneLoading}
        okButtonProps={{ disabled: cloneLoading }}
        cancelButtonProps={{ disabled: cloneLoading }}
      >
        <div style={{ opacity: cloneLoading ? 0.6 : 1, transition: 'opacity 0.3s' }}>
          <div style={{ marginBottom: '16px' }}>
            <Text>This will create a copy of:</Text>
            <div style={{ padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px', margin: '8px 0' }}>
              <Text strong>{sourceDocumentType?.document_type}</Text>
              <br />
              <Text type="secondary">{sourceDocumentType?.description}</Text>
            </div>
          </div>
          
          <div>
            <Text strong>New Document Type Name:</Text>
            <Input
              placeholder="Enter new document type name"
              value={newDocumentTypeName}
              onChange={(e) => setNewDocumentTypeName(e.target.value)}
              style={{ marginTop: '8px' }}
              onPressEnter={handleCloneConfirm}
              disabled={cloneLoading}
            />
            {cloneLoading && (
              <div style={{ marginTop: '12px', textAlign: 'center', color: '#1890ff' }}>
                <Text type="secondary">Creating clone... Please wait</Text>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Test Document Type Modal */}
      <Modal
        title="Test Document Type"
        open={testModalVisible}
        onCancel={handleTestModalClose}
        width={800}
        footer={[
          <Button key="cancel" onClick={handleTestModalClose}>
            Close
          </Button>,
          <Button
            key="test"
            type="primary"
            onClick={handleTestExecution}
            loading={testLoading}
            disabled={!uploadedFile}
          >
            Test Document
          </Button>
        ]}
      >
        {testingDocumentType && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>Testing: {testingDocumentType.document_type}</Title>
              <Text type="secondary">{testingDocumentType.description}</Text>
              <Divider />
              
              <div style={{ marginBottom: '16px' }}>
                <Text strong>Expected Fields:</Text>
                <div style={{ marginTop: '8px' }}>
                  {testingDocumentType.schema.fields.map(field => (
                    <Tag key={field.name} color="blue" style={{ margin: '2px' }}>
                      {field.name} ({field.type})
                    </Tag>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <Text strong>Upload Document to Test:</Text>
              <Upload.Dragger
                name="file"
                accept=".pdf,.jpg,.jpeg,.png,.tiff,.bmp"
                beforeUpload={(file) => {
                  setUploadedFile(file);
                  return false; // Prevent automatic upload
                }}
                onRemove={() => {
                  setUploadedFile(null);
                }}
                maxCount={1}
                style={{ marginTop: '8px' }}
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                <p className="ant-upload-hint">
                  Support for PDF, JPG, PNG, TIFF, BMP formats. Maximum file size: 10MB
                </p>
              </Upload.Dragger>
              
              {uploadedFile && (
                <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                  <Text strong>Selected file: </Text>
                  <Text>{uploadedFile.name} ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)</Text>
                </div>
              )}
            </div>

            {testResults && (
              <div>
                <Divider />
                <Title level={4}>Test Results</Title>
                
                <Row gutter={16} style={{ marginBottom: '16px' }}>
                  <Col span={8}>
                    <Card size="small">
                      <Statistic
                        title="Processing Time"
                        value={Math.floor(testResults.processing_metadata?.processing_time_ms || 0)}
                        suffix="ms"
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small">
                      <Statistic
                        title="Confidence Score"
                        value={(((testResults.confidence_score || 0)).toFixed(1))*100}
                        suffix="%"
                        valueStyle={{ 
                          color: (testResults.confidence_score || 0) > 80 ? '#3f8600' : 
                                 (testResults.confidence_score || 0) > 60 ? '#fa8c16' : '#cf1322' 
                        }}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small">
                      <Statistic
                        title="Fields Extracted"
                        value={testResults.extracted_fields ? Object.keys(testResults.extracted_fields).length : 0}
                        suffix={`/ ${testingDocumentType.schema.fields.length}`}
                      />
                    </Card>
                  </Col>
                </Row>

                {testResults.extracted_fields && (
                  <div>
                    <Text strong>Extracted Data:</Text>
                    <div style={{ marginTop: '8px', maxHeight: '300px', overflowY: 'auto' }}>
                      {Object.entries(testResults.extracted_fields).map(([fieldName, value]: [string, any]) => {
                        const fieldDef = testingDocumentType.schema.fields.find(f => f.name === fieldName);
                        return (
                          <Row key={fieldName} style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Col span={8}>
                              <Text strong>{fieldName}</Text>
                              {fieldDef && (
                                <div>
                                  <Tag color="blue">{fieldDef.type}</Tag>
                                  {fieldDef.required && <Tag color="red">Required</Tag>}
                                </div>
                              )}
                            </Col>
                            <Col span={16}>
                              <div style={{ 
                                padding: '4px 8px', 
                                backgroundColor: value ? '#f6ffed' : '#fff2f0',
                                borderRadius: '4px',
                                border: `1px solid ${value ? '#b7eb8f' : '#ffccc7'}`
                              }}>
                                <Text>{value || 'Not extracted'}</Text>
                                {value && typeof value === 'object' && (
                                  <div style={{ marginTop: '4px' }}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                      Confidence: {((value.confidence || 0) * 100).toFixed(1)}%
                                    </Text>
                                  </div>
                                )}
                              </div>
                            </Col>
                          </Row>
                        );
                      })}
                    </div>
                  </div>
                )}

                {testResults.errors && testResults.errors.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <Text strong style={{ color: '#cf1322' }}>Errors:</Text>
                    <ul style={{ marginTop: '8px', color: '#cf1322' }}>
                      {testResults.errors.map((error: string, index: number) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {testResults.warnings && testResults.warnings.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <Text strong style={{ color: '#fa8c16' }}>Warnings:</Text>
                    <ul style={{ marginTop: '8px', color: '#fa8c16' }}>
                      {testResults.warnings.map((warning: string, index: number) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
      </Content>
    </Layout>
  );
};

export default DocumentTypesPage;
